import { DeviceOS } from 'enums';

import { userDeviceUtils } from './user-device.utils';

/**
 * Renders a QR image centred on a white 1400×1400 canvas suitable for saving
 * to the device gallery. Cross-origin URLs are fetched through a blob to avoid
 * canvas taint; non-http(s) sources (data URLs, blob URLs) are used as-is.
 */
export async function createQrGalleryImage(sourceUrl: string): Promise<string> {
  let src = sourceUrl;
  let blobUrlToRevoke: string | undefined;

  if (sourceUrl.startsWith('http://') || sourceUrl.startsWith('https://')) {
    try {
      const blob = await (await fetch(sourceUrl)).blob();
      src = URL.createObjectURL(blob);
      blobUrlToRevoke = src;
    } catch {
      // continue with original URL
    }
  }

  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = src;
    });

    const size = 1400;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);
    const qrSize = Math.round(size * 0.5);
    const offset = Math.round((size - qrSize) / 2);
    ctx.drawImage(img, offset, offset, qrSize, qrSize);
    return canvas.toDataURL('image/jpeg', 1.0);
  } finally {
    if (blobUrlToRevoke) {
      URL.revokeObjectURL(blobUrlToRevoke);
    }
  }
}

export function downloadFile(url: string, filename: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/**
 * Save an image to the device. On iOS opens the native share sheet so the user can
 * save it to Photos (a plain download there lands in Files, not the gallery the bank
 * app reads from). On Android / desktop / in-app webviews it downloads the file directly.
 */
export async function saveImage(url: string, filename: string): Promise<void> {
  const deviceOS = userDeviceUtils.getDeviceOS();

  // iOS only: a download goes to Files, but the bank app picks the QR from Photos,
  // so we open the native share sheet ("Save Image"). Everywhere else (Android,
  // in-app webviews, desktop) a direct download is expected and works.
  if (deviceOS === DeviceOS.IOS) {
    let file: File | undefined;
    try {
      const blob = await (await fetch(url)).blob();
      file = new File([blob], filename, { type: blob.type || 'image/png' });
    } catch {
      file = undefined;
    }

    if (file && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file] });
        return;
      } catch (e) {
        // User dismissed the share sheet → don't download anything.
        if ((e as Error)?.name === 'AbortError') {
          return;
        }
        // Any other share failure → download the already-fetched blob via an
        // object URL (more reliable than re-downloading a data url).
        const objectUrl = URL.createObjectURL(file);
        downloadFile(objectUrl, filename);
        setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
        return;
      }
    }
  }

  // The `download` attribute is ignored for cross-origin URLs — the browser
  // navigates instead of saving. Fetching first produces a same-origin blob URL
  // that the browser will always download.
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      const blob = await (await fetch(url)).blob();
      const objectUrl = URL.createObjectURL(blob);
      downloadFile(objectUrl, filename);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
      return;
    } catch {
      // fallback to direct link if fetch fails
    }
  }

  downloadFile(url, filename);
}
